/**
 * Screen Wake Lock helper.
 * Keeps the screen on when an incoming call is shown or during an active call.
 * Note: Web apps cannot turn the screen ON when it is off; this only prevents
 * the screen from turning off while the page is visible.
 */

import { medicationRingtone } from '../../assets/audio';

let wakeLockSentinel = null;

/**
 * Request a screen wake lock so the device screen stays on.
 * Safe to call when already held or unsupported (no-op).
 */
export const requestScreenWakeLock = async () => {
  if (typeof navigator === 'undefined' || !navigator.wakeLock) return;
  try {
    if (wakeLockSentinel) return;
    wakeLockSentinel = await navigator.wakeLock.request('screen');
    wakeLockSentinel.addEventListener('release', () => {
      wakeLockSentinel = null;
    });
  } catch (err) {
    wakeLockSentinel = null;
  }
};

/**
 * Release the screen wake lock.
 */
export const releaseScreenWakeLock = async () => {
  try {
    if (wakeLockSentinel) {
      await wakeLockSentinel.release();
      wakeLockSentinel = null;
    }
  } catch (err) {
    wakeLockSentinel = null;
  }
};

/**
 * Vibrate the device when incoming call is received.
 * Skipped on iOS (vibration API is not supported in Safari/iOS).
 */
export const vibrateForIncomingCall = () => {
  if (typeof navigator === 'undefined' || !navigator.vibrate) return;
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (/\bMac\b/.test(ua) && navigator.maxTouchPoints > 1);
  if (isIOS) return;
  try {
    const tenSecondPattern = [];
    for (let i = 0; i < 10; i++) {
      tenSecondPattern.push(500, 500);
    }
    navigator.vibrate(tenSecondPattern);
  } catch (err) {
    // ignore
  }
};

/**
 * Stop vibration immediately (call when call is accepted, declined, or cancelled).
 */
export const stopVibration = () => {
  if (typeof navigator === 'undefined' || !navigator.vibrate) return;
  try {
    navigator.vibrate(0);
  } catch (_) {}
};

const RINGTONE_GLOBAL_KEY = '__incomingCallRingtone';
const RINGTONE_PRIME_GLOBAL_KEY = '__incomingCallRingtonePrime';
const DEFAULT_RINGTONE_URL = medicationRingtone;

const getResolvedRingtoneUrl = (ringtoneUrl = DEFAULT_RINGTONE_URL) => {
  if (typeof window === 'undefined') return ringtoneUrl;
  return new URL(ringtoneUrl, window.location.origin).toString();
};

const getRingtoneAudioElement = (url) => {
  if (typeof window === 'undefined' || typeof window.Audio === 'undefined') {
    return null;
  }

  const primeState = window[RINGTONE_PRIME_GLOBAL_KEY] || {};

  if (!primeState.audio || primeState.audio.src !== url) {
    primeState.audio = new Audio(url);
    primeState.audio.preload = 'auto';
    primeState.audio.loop = true;
    primeState.audio.playsInline = true;
  }

  window[RINGTONE_PRIME_GLOBAL_KEY] = primeState;
  return primeState.audio;
};

const getAudioContext = () => {
  if (typeof window === 'undefined' || (!window.AudioContext && !window.webkitAudioContext)) return;

  const primeState = window[RINGTONE_PRIME_GLOBAL_KEY] || {};

  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!primeState.ctx || primeState.ctx.state === 'closed') {
      primeState.ctx = new Ctx();
    }
    window[RINGTONE_PRIME_GLOBAL_KEY] = primeState;
    return primeState.ctx;
  } catch (_) {
    return null;
  }
};

const getPrimedAudioContext = () => {
  const primeState = typeof window !== 'undefined' ? window[RINGTONE_PRIME_GLOBAL_KEY] : null;
  const ctx = primeState?.ctx;
  return ctx && ctx.state === 'running' ? ctx : null;
};

const getDecodedRingtoneBuffer = async (ctx, url) => {
  if (!ctx) return null;

  const primeState = window[RINGTONE_PRIME_GLOBAL_KEY] || {};

  if (primeState.buffer && primeState.bufferUrl === url) {
    return primeState.buffer;
  }

  if (!primeState.bufferPromise || primeState.bufferUrl !== url) {
    primeState.bufferUrl = url;
    primeState.bufferPromise = fetch(url)
      .then((res) => res.arrayBuffer())
      .then((buf) => ctx.decodeAudioData(buf))
      .then((buffer) => {
        const latestPrimeState = window[RINGTONE_PRIME_GLOBAL_KEY] || {};
        latestPrimeState.buffer = buffer;
        latestPrimeState.bufferUrl = url;
        latestPrimeState.bufferPromise = null;
        window[RINGTONE_PRIME_GLOBAL_KEY] = latestPrimeState;
        return buffer;
      })
      .catch((error) => {
        const latestPrimeState = window[RINGTONE_PRIME_GLOBAL_KEY] || {};
        latestPrimeState.bufferPromise = null;
        window[RINGTONE_PRIME_GLOBAL_KEY] = latestPrimeState;
        throw error;
      });
  }

  window[RINGTONE_PRIME_GLOBAL_KEY] = primeState;
  return primeState.bufferPromise;
};

const playRingtoneWithWebAudio = (url) => {
  if (typeof window === 'undefined') return;

  try {
    const ctx = getPrimedAudioContext();
    if (!ctx) return;

    const source = ctx.createBufferSource();

    window[RINGTONE_GLOBAL_KEY] = { ctx, source };

    getDecodedRingtoneBuffer(ctx, url)
      .then(async (buffer) => {
        const ref = window[RINGTONE_GLOBAL_KEY];
        if (!ref || ref.ctx !== ctx) return;

        ref.source.buffer = buffer;
        ref.source.loop = true;
        ref.source.connect(ctx.destination);

        if (ctx.state === 'suspended') {
          await ctx.resume();
        }

        ref.source.start(0);
      })
      .catch(() => {
        window[RINGTONE_GLOBAL_KEY] = null;
      });
  } catch (err) {
    window[RINGTONE_GLOBAL_KEY] = null;
  }
};

export const primeRingtoneAudio = (ringtoneUrl = DEFAULT_RINGTONE_URL) => {
  if (typeof window === 'undefined') return;

  const primeState = window[RINGTONE_PRIME_GLOBAL_KEY] || {};

  // Debounce: priming only needs to run occasionally to keep audio autoplay
  // unlocked. It is wired to fire on every user gesture (click/pointer/touch/
  // keydown, capture phase) AND can be driven indirectly by realtime activity.
  // A burst of those fires primeRingtoneAudio many times within a few
  // milliseconds, and each call does a muted play() -> pause() that re-activates
  // the iOS AVAudioSession. That rapid activate/deactivate thrash starves the
  // WKWebView main thread and freezes the whole screen on iOS (scroll/taps stop
  // responding until interaction resumes). Android has no AVAudioSession, so it
  // is unaffected — which is exactly the observed "iOS-only" freeze. Collapsing
  // the burst to one prime per interval removes the thrash with no functional
  // loss (autoplay stays unlocked once primed).
  const now = Date.now();
  if (primeState.lastPrimeAt && now - primeState.lastPrimeAt < 2000) {
    return;
  }
  primeState.lastPrimeAt = now;
  window[RINGTONE_PRIME_GLOBAL_KEY] = primeState;

  const url = getResolvedRingtoneUrl(ringtoneUrl);
  const audio = getRingtoneAudioElement(url);

  const ctx = getAudioContext();
  if (ctx) {
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }
    getDecodedRingtoneBuffer(ctx, url).catch(() => {});
  }

  if (audio) {
    try {
      const previousMuted = audio.muted;
      const previousVolume = audio.volume;

      audio.muted = true;
      audio.volume = 0;
      audio
        .play()
        .then(() => {
          audio.pause();
          audio.currentTime = 0;
          audio.muted = previousMuted;
          audio.volume = previousVolume || 1;
          primeState.isPrimed = true;
        })
        .catch(() => {
          audio.muted = previousMuted;
          audio.volume = previousVolume || 1;
        });
    } catch (_) {}
  }

  window[RINGTONE_PRIME_GLOBAL_KEY] = primeState;
};

const installRingtonePrimer = () => {
  if (typeof window === 'undefined' || window.__incomingCallRingtonePrimerInstalled) return;

  window.__incomingCallRingtonePrimerInstalled = true;

  const primeFromUserGesture = () => {
    primeRingtoneAudio();
  };

  ['click', 'pointerdown', 'touchstart', 'keydown'].forEach((eventName) => {
    window.addEventListener(eventName, primeFromUserGesture, {
      capture: true,
      passive: true,
    });
  });
};

installRingtonePrimer();

/**
 * Play incoming-call ringtone when the app is in the foreground.
 * Uses the bundled medication ringtone for all incoming video calls.
 * Stops when stopRingtone() is called (accept/decline/close).
 */
export const playRingtone = (ringtoneUrl = DEFAULT_RINGTONE_URL) => {
  if (typeof window === 'undefined') return;

  try {
    stopRingtone();
    const url = getResolvedRingtoneUrl(ringtoneUrl);
    const ctx = getPrimedAudioContext();

    if (ctx) {
      playRingtoneWithWebAudio(url);
      return;
    }

    const audio = getRingtoneAudioElement(url);

    if (!audio) {
      playRingtoneWithWebAudio(url);
      return;
    }

    audio.pause();
    audio.currentTime = 0;
    audio.loop = true;
    audio.muted = false;
    audio.volume = 1;

    window[RINGTONE_GLOBAL_KEY] = { audio };

    audio
      .play()
      .catch(() => {
        const ref = window[RINGTONE_GLOBAL_KEY];
        if (ref?.audio === audio && getPrimedAudioContext()) {
          try {
            audio.pause();
            audio.currentTime = 0;
          } catch (_) {}
          playRingtoneWithWebAudio(url);
        }
      });
  } catch (err) {
    window[RINGTONE_GLOBAL_KEY] = null;
  }
};

/**
 * Stop the incoming-call ringtone (call on accept/decline/close).
 */
export const stopRingtone = () => {
  try {
    const ref = typeof window !== 'undefined' ? window[RINGTONE_GLOBAL_KEY] : null;

    if (ref?.audio) {
      try {
        ref.audio.pause();
        ref.audio.currentTime = 0;
      } catch (_) {}
    }

    if (ref && ref.source) {
      try {
        ref.source.stop();
        ref.source.disconnect();
      } catch (_) {}
    }

    if (typeof window !== 'undefined') window[RINGTONE_GLOBAL_KEY] = null;
  } catch (err) {
    if (typeof window !== 'undefined') window[RINGTONE_GLOBAL_KEY] = null;
  }
};
